// @ts-check

import { toKebabCase } from "#/shared/utils/string";
import {
  CUSTOM_VALIDITY_EVENT,
  FORM_DISABLED_EVENT,
  FORM_RESET_EVENT,
  NAME_CHANGED_EVENT,
  RECONNECTED_EVENT,
} from "./events";
import { extractComponentName } from "./utils";

/**
 * The registration-owned tag set: every tag this layer actually defined —
 * canonical tags at boot, aliases at config arrival. A tag that was already in
 * the browser registry when we got to it (a third party claimed the name
 * first) is deliberately NOT recorded: such an element never receives our
 * mount-ready/failed markers, so any consumer that treated it as ours would
 * wait on signals that can never fire.
 *
 * @type {Set<string>}
 */
const registeredTags = new Set();

/**
 * Registers a custom element.
 *
 * The name of the custom element will be derived from the custom element file name.
 * The custom element will be registered under the "sp-" prefix.
 * For example, the custom element file name "MyComponent.ce.vue" will be registered as "sp-my-component".
 *
 * Two independent concerns are applied here:
 *
 *  - **Disabled normalization is universal.** Every registered `sp-*` element
 *    normalizes a falsy `disabled` (`false` / `"false"`) to the attribute being
 *    absent, so a stray `disabled="false"` (authored, or from Vue's attribute
 *    fallback during the upgrade-ordering race) never disables the element —
 *    whether it keys off the native `:disabled` pseudo, a `:host([disabled])`
 *    attribute selector, or `hasAttribute("disabled")`.
 *  - **Form association is opt-in.** Only components that declare
 *    `formAssociated: true` additionally become form-associated custom elements
 *    (match `:disabled`, respond to `<fieldset disabled>`, submit a value).
 *
 * @param {string} fileName
 * @param {import("vue").VueElementConstructor<{}>} elementConstructor
 * @param {{ formAssociated?: boolean }} [options]
 *
 * @see https://html.spec.whatwg.org/multipage/custom-elements.html#concept-element-form-associated
 */
export function registerCustomElement(fileName, elementConstructor, options) {
  const { formAssociated = false } = options ?? {};
  const elementName = toKebabCase(extractComponentName(fileName));
  const isAlreadyRegistered = customElements.get(elementName);

  if (isAlreadyRegistered) {
    return;
  }

  customElements.define(elementName, createElementClass(elementConstructor, formAssociated));
  registeredTags.add(elementName);
}

/**
 * Whether this registration layer defined the tag — the classifier the form
 * guard uses on `form.elements` members. Stricter than `customElements.get()`:
 * a host page's own custom elements (including third-party form-associated
 * ones) are defined in the registry but are not ours, must never be treated as
 * pending or failed catalog controls, and must never stall a submit.
 *
 * @param {string} tagName - a lowercase tag name (e.g. an element's `localName`).
 * @returns {boolean}
 */
export function isRegisteredTag(tagName) {
  return registeredTags.has(tagName);
}

/**
 * @typedef {{
 *   _instance?: unknown,
 *   formDisabledCallback?(disabled: boolean): void,
 *   formResetCallback?(): void,
 * }} FormHostHooks
 */

/**
 * Selects the element class to register. Every element gets the disabled
 * normalization; form-associated elements get the additional native behaviour.
 *
 * @param {import("vue").VueElementConstructor<{}>} elementConstructor
 * @param {boolean} formAssociated
 * @returns {import("vue").VueElementConstructor<{}>}
 */
function createElementClass(elementConstructor, formAssociated) {
  if (formAssociated) {
    return createFormAssociatedClass(elementConstructor);
  }

  return createNormalizedClass(elementConstructor);
}

/**
 * Wraps a custom element constructor so a falsy `disabled` attribute is normalized
 * away. `disabled` is a presence-based boolean attribute, so any value — including
 * the string `"false"` — would otherwise count as "disabled" for native `:disabled`,
 * `:host([disabled])` selectors, and `hasAttribute` checks. Applied to every
 * registered element regardless of form association.
 *
 * @param {import("vue").VueElementConstructor<FormHostHooks>} Base
 */
function createNormalizedClass(Base) {
  return class extends Base {
    /**
     * Authored markup (`<sp-text-field disabled="false">`) sets the attribute
     * through the parser, which bypasses {@link setAttribute}. Normalize it on
     * connection. Runtime/programmatic writes are handled by {@link setAttribute}.
     */
    connectedCallback() {
      if (this.getAttribute("disabled") === "false") {
        this.removeAttribute("disabled");
      }

      // A mounted instance survives a synchronous detach/re-insert (Vue defers the unmount by a
      // tick), so this call is a reconnection rather than a first mount.
      const reconnected = Boolean(this._instance);

      super.connectedCallback?.();

      if (reconnected) {
        this.dispatchEvent(new Event(RECONNECTED_EVENT));
      }
    }

    /**
     * A falsy `disabled` (`false` / `"false"`) is normalized to the attribute
     * being absent so it never reads as disabled anywhere.
     *
     * @param {string} name
     * @param {*} value
     */
    setAttribute(name, value) {
      // Attribute names are ASCII case-insensitive, so the token is matched lowercased.
      if (String(name).toLowerCase() === "disabled" && (value === false || value === "false")) {
        this.removeAttribute("disabled");
        return;
      }

      super.setAttribute(name, value);
    }
  };
}

/**
 * Extends the normalized wrapper with form-associated behaviour.
 *
 * `formAssociated` must be a static flag on the constructor because the browser
 * reads it once at `customElements.define()` time; it cannot be toggled per
 * instance. The wrapper bridges the native lifecycle callbacks (`formDisabledCallback`,
 * `formResetCallback`) into the Vue component as DOM events, which Vue itself
 * does not forward.
 *
 * `attachInternals()` throws on a second call, so the wrapper owns the single
 * attachment and `useForm` reads it back through `formInternals`. That ownership
 * is also what lets the host carry the native validation surface
 * (`checkValidity`, `reportValidity`, `validity`, `validationMessage`,
 * `willValidate`), which `ElementInternals` otherwise keeps to itself.
 *
 * @param {import("vue").VueElementConstructor<FormHostHooks>} Base
 */
function createFormAssociatedClass(Base) {
  return class extends createNormalizedClass(Base) {
    static formAssociated = true;

    /** @type {ElementInternals | null} */
    formInternals = null;

    constructor() {
      super();

      // jsdom's `ElementInternals` carries the ARIA reflection but none of the
      // form-association surface, so only internals that can hold a form value count.
      try {
        const internals = this.attachInternals();
        this.formInternals = typeof internals.setFormValue === "function" ? internals : null;
      } catch {
        this.formInternals = null;
      }
    }

    /**
     * Invoked by the browser when the element's disabled state changes, including
     * when an ancestor `<fieldset>` is disabled. Re-emitted as a DOM event so the
     * component can react without the wrapper mutating the host instance. Disabled
     * styling needs no JS — style `:host(:disabled)`.
     *
     * @param {boolean} disabled
     */
    formDisabledCallback(disabled) {
      super.formDisabledCallback?.(disabled);
      this.dispatchEvent(new CustomEvent(FORM_DISABLED_EVENT, { detail: disabled }));
    }

    /**
     * Invoked by the browser when the owning form is reset.
     *
     * @see https://html.spec.whatwg.org/multipage/custom-elements.html#face-form-reset-callback
     */
    formResetCallback() {
      super.formResetCallback?.();
      this.dispatchEvent(new CustomEvent(FORM_RESET_EVENT));
    }

    /**
     * The custom error a consumer set, and the flags the component owns. They are held apart
     * because `ElementInternals.setValidity` replaces the whole flag set, so each writer would
     * otherwise erase the other's.
     */
    #customError = "";
    /** @type {{ flags: Partial<Record<keyof ValidityStateFlags, boolean>>, message: string, anchor?: HTMLElement }} */
    #controlValidity = { flags: {}, message: "", anchor: undefined };

    get form() {
      return this.formInternals?.form ?? null;
    }

    get labels() {
      return this.formInternals?.labels ?? null;
    }

    get name() {
      return this.getAttribute("name") ?? "";
    }

    // Stringified the way WebIDL does for a non-nullable DOMString, so `null` reads as `"null"`.
    set name(value) {
      this.setAttribute("name", String(value));
    }

    /**
     * Announces a `name` write in the same task, so a control that bakes the name into its own
     * FormData rebuilds before the next statement runs. Covers the property, which writes through
     * here, and a direct attribute write under any casing. The detail comes from the attribute:
     * a declared `name` prop installs its own non-configurable accessor over the getter below.
     *
     * @param {string} attribute
     * @param {*} value
     */
    setAttribute(attribute, value) {
      super.setAttribute(attribute, value);

      if (String(attribute).toLowerCase() === "name") {
        this.dispatchEvent(new CustomEvent(NAME_CHANGED_EVENT, { detail: this.getAttribute("name") ?? "" }));
      }
    }

    /**
     * @param {string} attribute
     */
    removeAttribute(attribute) {
      super.removeAttribute(attribute);

      if (String(attribute).toLowerCase() === "name") {
        this.dispatchEvent(new CustomEvent(NAME_CHANGED_EVENT, { detail: "" }));
      }
    }

    get readOnly() {
      return this.hasAttribute("readonly");
    }

    set readOnly(value) {
      this.toggleAttribute("readonly", Boolean(value));
    }

    /**
     * Sets or clears the consumer's own validation error, an empty string clearing it. The
     * argument is stringified the way WebIDL does, so `null` reads as `"null"`.
     *
     * @param {string} message
     */
    setCustomValidity(message) {
      this.#customError = String(message);
      this.#applyValidity();
      this.dispatchEvent(new CustomEvent(CUSTOM_VALIDITY_EVENT, { detail: this.#customError }));
    }

    /**
     * The seam `useForm().setValidity` writes through, so the component's flags and the custom
     * error survive each other. Not part of the native surface.
     *
     * @param {Partial<Record<keyof ValidityStateFlags, boolean>>} flags
     * @param {string} [message]
     * @param {HTMLElement} [anchor]
     */
    setControlValidity(flags, message, anchor) {
      this.#controlValidity = { flags: flags ?? {}, message: message ?? "", anchor };
      this.#applyValidity();
    }

    #applyValidity() {
      if (!this.formInternals) {
        return;
      }

      const { flags, message, anchor } = this.#controlValidity;

      if (this.#customError) {
        this.formInternals.setValidity({ ...flags, customError: true }, this.#customError, anchor);
      } else if (Object.values(flags).some(Boolean)) {
        this.formInternals.setValidity(flags, message, anchor);
      } else {
        this.formInternals.setValidity({});
      }
    }

    get willValidate() {
      return this.formInternals?.willValidate ?? false;
    }

    get validity() {
      return this.formInternals?.validity;
    }

    // Empty while the element is barred from constraint validation, matching the native getter.
    get validationMessage() {
      return this.willValidate ? /** @type {ElementInternals} */ (this.formInternals).validationMessage : "";
    }

    checkValidity() {
      return this.formInternals?.checkValidity() ?? true;
    }

    reportValidity() {
      return this.formInternals?.reportValidity() ?? true;
    }
  };
}
